import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ShoppingCart, Search, Filter, Plus, FileText, Download, Trash2, Edit2, Calendar, DollarSign, Building2, Package, CheckCircle, Clock, XCircle, AlertCircle, Upload, Save, FileCode, Paperclip, Link as LinkIcon, Briefcase, AlertTriangle, AlertOctagon, Sparkles, Loader2, Image as ImageIcon, MoreHorizontal, List, CheckSquare, Activity, ArrowRight, Gavel } from 'lucide-react';
import './licitacoes-redesign.css';
import ClearFiltersButton from './ClearFiltersButton';
import MoneyInput from './inputs/MoneyInput';
import OrderKanban from './OrderKanban';
import useCompanies from '../hooks/useCompanies';

import { useModal } from './ModalProvider';

import { PurchaseOrder, OrderType, OrderStatus, CompanyProfile, AnalyzedFile, Tender, TenderStatus, OrderDocumentType } from '@/types';
import { logActivity } from '@/services/auditService';
// TODO: Implement extractOrderData via API call to backend
// import { extractOrderData } from '@/services/geminiService';
import { apiService } from '@/services/apiService';
import { daysUntilCalendarDate, formatCalendarDate } from '@/utils/date';

// Helper: renders an image stored in S3 by fetching a presigned URL on mount
const CommentAttachmentImage: React.FC<{ s3Key: string; name: string }> = ({ s3Key, name }) => {
    const [src, setSrc] = React.useState<string | null>(null);
    React.useEffect(() => {
        let cancelled = false;
        apiService.getPresignedDownloadUrl(s3Key).then(({ downloadUrl }) => {
            if (!cancelled) setSrc(downloadUrl);
        }).catch(() => {/* ignore */});
        return () => { cancelled = true; };
    }, [s3Key]);
    if (!src) return <div className="h-16 w-16 bg-surface-alt dark:bg-surface-dark-alt rounded animate-pulse" />;
    return <img src={src} alt={name} className="h-16 w-16 object-cover rounded" />;
};

// Helper: renders an audio player stored in S3 by fetching a presigned URL on mount
const CommentAttachmentAudio: React.FC<{ s3Key: string; name: string }> = ({ s3Key, name }) => {
    const [src, setSrc] = React.useState<string | null>(null);
    React.useEffect(() => {
        let cancelled = false;
        apiService.getPresignedDownloadUrl(s3Key).then(({ downloadUrl }) => {
            if (!cancelled) setSrc(downloadUrl);
        }).catch(() => {/* ignore */});
        return () => { cancelled = true; };
    }, [s3Key]);
    return (
        <div className="w-full flex items-center gap-2 bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark rounded-lg px-3 py-2 min-w-[220px]">
            <span title={name}>🎙️</span>
            {src ? (
                /* eslint-disable-next-line jsx-a11y/media-has-caption */
                <audio src={src} controls className="flex-1 h-8 min-w-0" />
            ) : (
                <div className="flex-1 h-8 bg-surface-alt dark:bg-surface-dark-alt rounded animate-pulse" />
            )}
        </div>
    );
};

const OrderManager: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const { companies, loading: companiesLoading, refresh: refreshCompanies, getCompanyName } = useCompanies();
  const [wonTenders, setWonTenders] = useState<Tender[]>([]);
    const location = useLocation();
    const navigate = useNavigate();
    const [focusCommentId, setFocusCommentId] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [docTypeFilter, setDocTypeFilter] = useState<OrderDocumentType | 'ALL'>('ALL');
  const [companyFilter, setCompanyFilter] = useState<string>('ALL'); // Filtro de Empresa adicionado

  // Novos Filtros de Prazo
  const [deadlineStart, setDeadlineStart] = useState('');
  const [deadlineEnd, setDeadlineEnd] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState<'ALL' | 'EXPIRED' | 'URGENT' | 'WARNING'>('ALL');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null);
    // Kanban detail modal state (Trello-like) — only used when viewMode === 'kanban'
    const [isKanbanModalOpen, setIsKanbanModalOpen] = useState(false);
    const [kanbanOrder, setKanbanOrder] = useState<PurchaseOrder | null>(null);
  const [formData, setFormData] = useState<Partial<PurchaseOrder>>({
    number: '',
    title: '',
    type: 'PRODUCT',
    documentType: 'EMPENHO',
    supplier: '',
    tenderId: '',
    requestDate: new Date().toISOString().split('T')[0],
    value: 0,
    status: 'PENDING',
    description: '',
    companyId: ''
  });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    // For create-flow: allow selecting multiple files before saving
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    // Kanban modal comments state
    const [kanbanComments, setKanbanComments] = useState<any[]>([]);
    const [kanbanLoadingComments, setKanbanLoadingComments] = useState(false);
    const [kanbanNewComment, setKanbanNewComment] = useState('');
    const [kanbanPostingComment, setKanbanPostingComment] = useState(false);
    // Attachments for new comment (images, files)
    const [commentFiles, setCommentFiles] = useState<File[]>([]);
    const [commentPreviews, setCommentPreviews] = useState<{ name: string; url: string; isImage: boolean }[]>([]);
    const commentFileInputRef = useRef<HTMLInputElement | null>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const COMMON_EMOJIS = ['😀','😂','😍','🤔','👍','👎','🙏','🎉','🚀','✅','❌','⚠️','📎','🔍','💬','📅','💰','📋','🏆','⏰'];
    // Audio recording state
    const [audioRecording, setAudioRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioDuration, setAudioDuration] = useState(0); // seconds
    const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    // UI states for premium modal
    const [kanbanEditingDescription, setKanbanEditingDescription] = useState(false);
    const [kanbanDescriptionDraft, setKanbanDescriptionDraft] = useState('');
    const [kanbanTitleDraft, setKanbanTitleDraft] = useState('');
    const [kanbanDropHover, setKanbanDropHover] = useState(false);

    // Meta fields editing (supplier, documentType, requestDate) inside Kanban modal
    const [kanbanEditingMeta, setKanbanEditingMeta] = useState(false);
    const [kanbanMetaDraft, setKanbanMetaDraft] = useState<{ supplier: string; documentType: string; requestDate: string }>({ supplier: '', documentType: 'EMPENHO', requestDate: '' });
    const [kanbanSavingMeta, setKanbanSavingMeta] = useState(false);

    // Menções (autocomplete) dentro do modal Kanban
    const [kanbanUsers, setKanbanUsers] = useState<any[]>([]); // lista de usuários do sistema (para mencionar)
    const [kanbanMentions, setKanbanMentions] = useState<any[]>([]); // usuários mencionados no rascunho atual
    const [mentionSuggestions, setMentionSuggestions] = useState<any[]>([]);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
    const kanbanCommentRef = useRef<HTMLTextAreaElement | null>(null);
    const [suggestionPortalStyle, setSuggestionPortalStyle] = useState<{ top: number; left: number; width: number } | null>(null);

    // Busca usuários quando o modal kanban abre (lazy)
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!isKanbanModalOpen) return;
            try {
                const users = await apiService.getMentionableUsers();
                if (!mounted) return;
                setKanbanUsers(users || []);
            } catch (err) {
                console.warn('Não foi possível buscar usuários para menções:', err);
            }
        };
        load();
        return () => { mounted = false; };
    }, [isKanbanModalOpen]);

    // Insere menção no formato @FullName (sem colchetes) e registra o id no kanbanMentions
    const insertMention = (user: any) => {
        const el = kanbanCommentRef.current;
        const text = kanbanNewComment || '';
        if (!el) {
            // fallback: append
            const next = `${text} @[${user.name}] `;
            setKanbanNewComment(next);
            setKanbanMentions(prev => Array.from(new Map([...prev, user].map((u:any)=>[u.id,u])).values()));
            setShowMentionSuggestions(false);
            return;
        }

        const pos = el.selectionStart || text.length;
        // find the last '@' before cursor that starts a mention (preceded by whitespace or line start)
        let atIndex = text.lastIndexOf('@', pos - 1);
        while (atIndex > 0 && /[^\s]/.test(text[atIndex - 1])) {
            // if char before @ is not whitespace, it's probably not a mention start -> break search
            atIndex = text.lastIndexOf('@', atIndex - 1);
        }
        if (atIndex === -1) {
            // append at end
            const next = `${text} @[${user.name}] `;
            setKanbanNewComment(next);
            setKanbanMentions(prev => Array.from(new Map([...prev, user].map((u:any)=>[u.id,u])).values()));
            setShowMentionSuggestions(false);
            setTimeout(() => el.focus(), 0);
            return;
        }

        const before = text.slice(0, atIndex);
        const after = text.slice(pos);
    const inserted = `@${user.name} `;
        const next = before + inserted + after;
        setKanbanNewComment(next);
        // move cursor after inserted mention
        setTimeout(() => {
            const newPos = (before + inserted).length;
            el.focus();
            el.selectionStart = el.selectionEnd = newPos;
        }, 0);

        // add to mentions list (avoid duplicates)
        setKanbanMentions(prev => {
            if (prev.find(p => p.id === user.id)) return prev;
            return [...prev, user];
        });
        setShowMentionSuggestions(false);
    };

    // Handle textarea change and detect @query
    const handleKanbanCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        setKanbanNewComment(val);
        const pos = e.target.selectionStart || val.length;

        // find last '@' before cursor which starts a mention
        const at = val.lastIndexOf('@', pos - 1);
        if (at === -1) {
            setShowMentionSuggestions(false);
            setMentionQuery('');
            return;
        }

        // only trigger when @ is not immediately preceded by non-whitespace (to avoid emails etc.)
        if (at > 0 && /\S/.test(val[at - 1])) {
            setShowMentionSuggestions(false);
            setMentionQuery('');
            return;
        }

        const q = val.slice(at + 1, pos);
        setMentionQuery(q);
        // filter users by name or email
        const qs = q.trim().toLowerCase();
        const suggestions = qs.length === 0 ? kanbanUsers.slice(0, 8) : kanbanUsers.filter(u => (u.name || '').toLowerCase().includes(qs) || (u.email || '').toLowerCase().includes(qs)).slice(0, 8);
        setMentionSuggestions(suggestions);
        setSelectedSuggestionIndex(0);
        setShowMentionSuggestions(suggestions.length > 0);
        // calculate portal position so dropdown can overlay modal and appear above textarea
        if (suggestions.length > 0 && kanbanCommentRef.current) {
            const r = kanbanCommentRef.current.getBoundingClientRect();
            setSuggestionPortalStyle({ top: r.top, left: r.left, width: r.width });
        } else {
            setSuggestionPortalStyle(null);
        }
    };

    const handleKanbanCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showMentionSuggestions) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestionIndex(i => Math.min(i + 1, (mentionSuggestions.length || 1) - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestionIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            // if suggestions visible, select currently highlighted
            if (mentionSuggestions.length > 0) {
                e.preventDefault();
                const user = mentionSuggestions[selectedSuggestionIndex];
                if (user) insertMention(user);
            }
        } else if (e.key === 'Escape') {
            setShowMentionSuggestions(false);
        }
    };

    // Render comment content with highlighted mentions: looks for pattern @[Full Name]
    const renderCommentContent = (content: string) => {
        if (!content) return null;
        const parts: React.ReactNode[] = [];
        // Regex to find @[...]
        const regex = /@\[([^\]]+)\]/g;
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = regex.exec(content)) !== null) {
            const idx = m.index;
            if (idx > lastIndex) parts.push(content.slice(lastIndex, idx));
            const name = m[1];
            parts.push(
                <span key={`${name}-${idx}`} className="text-blue-600 dark:text-blue-300 font-medium bg-blue-50 dark:bg-blue-900/30 px-1 rounded">@{name}</span>
            );
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < content.length) parts.push(content.slice(lastIndex));
        // fallback: if no bracket mentions found, try to highlight simple @Name tokens (single-word)
        if (parts.length === 1 && typeof parts[0] === 'string') {
            const simple = content.split(/(\@[A-Za-z0-9_\-\.]+)/g).map((chunk, i) => {
                if (chunk.startsWith('@')) return <span key={i} className="text-blue-600 dark:text-blue-300 font-medium">{chunk}</span>;
                return <React.Fragment key={i}>{chunk}</React.Fragment>;
            });
            return <>{simple}</>;
        }
        return <>{parts}</>;
    };

    // Ref + auto-resize for kanban title editor
    const kanbanTitleRef = useRef<HTMLTextAreaElement | null>(null);
    const adjustKanbanTitleHeight = () => {
        const el = kanbanTitleRef.current;
        if (!el) return;
        try {
            const MAX_HEIGHT = 420; // px - keeps it inside modal
            el.style.height = 'auto';
            const desired = Math.max(40, el.scrollHeight);
            if (desired > MAX_HEIGHT) {
                el.style.height = `${MAX_HEIGHT}px`;
                el.style.overflowY = 'auto';
            } else {
                el.style.height = `${desired}px`;
                el.style.overflowY = 'hidden';
            }
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        if (kanbanEditingDescription) {
            // allow layout to settle then adjust and focus
            setTimeout(() => {
                adjustKanbanTitleHeight();
                kanbanTitleRef.current?.focus();
            }, 0);
        }
    }, [kanbanEditingDescription]);

    useEffect(() => {
        // adjust as content changes
        adjustKanbanTitleHeight();
    }, [kanbanTitleDraft]);

    // Estado para menu de ações por linha
    const [openActionsId, setOpenActionsId] = useState<string | null>(null);
    const [actionsAnchorRect, setActionsAnchorRect] = useState<{ top: number; left: number } | null>(null);

    // Fecha menu de ações ao clicar fora
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target) return;
            if (target.closest('.actions-menu') || target.closest('.actions-button')) return;
            if (openActionsId) {
                setOpenActionsId(null);
                setActionsAnchorRect(null);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [openActionsId]);

  // Estados da IA
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);

    const { showAlert, showConfirm } = useModal();
    // toggle entre visualização em lista e quadro Kanban
    const [viewMode, setViewMode] = useState<'list' | 'cards' | 'kanban'>('list');

    // Visual-only: sempre entra na tela em modo Lista (exceto deep-link ?openOrder= que força kanban).
    useEffect(() => {
        setViewMode('list');
    }, []);

    // Visual-only: chips rápidos de Prazo e Status (mesmo padrão da tela Licitações)
    const [quickPrazo, setQuickPrazo] = useState<'ALL' | 'HOJE' | '7D' | '30D'>('ALL');
    const [quickStatus, setQuickStatus] = useState<'ALL' | 'VENCIDO' | 'PENDENTE' | 'EXEC' | 'COMPLETO'>('ALL');

    // Chips de Prazo mapeiam para urgencyFilter existente
    useEffect(() => {
        if (quickPrazo === 'ALL') setUrgencyFilter('ALL');
        else if (quickPrazo === 'HOJE') setUrgencyFilter('EXPIRED');
        else if (quickPrazo === '7D') setUrgencyFilter('URGENT');
        else if (quickPrazo === '30D') setUrgencyFilter('WARNING');
    }, [quickPrazo]);

    // Chips de Status mapeiam para statusFilter existente
    useEffect(() => {
        if (quickStatus === 'ALL') setStatusFilter('ALL');
        else if (quickStatus === 'VENCIDO') setStatusFilter('OVERDUE' as OrderStatus);
        else if (quickStatus === 'PENDENTE') setStatusFilter('PENDING' as OrderStatus);
        else if (quickStatus === 'EXEC') setStatusFilter('IN_PROGRESS' as OrderStatus);
        else if (quickStatus === 'COMPLETO') setStatusFilter('COMPLETED' as OrderStatus);
    }, [quickStatus]);

    const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
        try {
            const order = orders.find(o => o.id === orderId);
            if (!order) return;
            // Only send the changed field to the API. Sending the whole `order` object
            // may include empty or stale foreign-key fields (companyId, tenderId, etc.)
            // which can trigger the backend FK validation error observed.
            console.info('handleStatusChange: sending status', { orderId, newStatus });
            const resp = await apiService.updateOrder(orderId, { status: newStatus });
            console.info('handleStatusChange: api response', resp);

            // If the backend returned the order but the status wasn't updated,
            // surface a helpful message — this usually means the backend rejected
            // the transition or ignores status changes via this endpoint.
            if (resp && resp.status && resp.status !== newStatus) {
                await showAlert(`Servidor não aplicou a transição de status: solicitado ${newStatus}, retornado ${resp.status}. Verifique regras de transição no backend.`);
            }

            // Refresh local list
            setOrders(await apiService.getOrders());
        } catch (err) {
            console.error('Erro ao atualizar status do pedido:', err);
            await showAlert('Erro ao mover cartão. Tente novamente.');
        }
    };

    // quando a lista de companies for atualizada, preenche o companyId padrão no form
    useEffect(() => {
        if (companies && companies.length > 0 && !editingOrder) {
            const defaultComp = companies.find((c: CompanyProfile) => c.isDefault);
            setFormData(prev => ({ ...prev, companyId: prev.companyId || (defaultComp ? defaultComp.id : companies[0].id) }));
        }
    }, [companies, editingOrder]);
  useEffect(() => {
    // Carregar Ordens (fonte única: backend). Removida migração automática do localStorage.
        const loadOrders = async () => {
            try {
                const data = await apiService.getOrders();

                // Clean up any legacy localStorage key if present (we no longer persist orders client-side).
                try {
                    if (typeof localStorage !== 'undefined' && localStorage.getItem('licitaflow_orders')) {
                        localStorage.removeItem('licitaflow_orders');
                        console.info('Removed legacy localStorage key: licitaflow_orders');
                    }
                } catch (e) { /* ignore localStorage errors */ }

                setOrders(data);
            } catch (err) {
                console.error('Erro ao carregar ordens:', err);
            }
        };
    loadOrders();

        // companies are loaded via shared hook
        if (companies && companies.length > 0 && !editingOrder) {
            const defaultComp = companies.find((c: CompanyProfile) => c.isDefault);
            setFormData(prev => ({ ...prev, companyId: prev.companyId || (defaultComp ? defaultComp.id : companies[0].id) }));
        }

    // Carregar Licitações Ganhas para Vínculo
    const loadTenders = async () => {
        try {
            const allTenders = await apiService.getTenders();
            setWonTenders(allTenders.filter(t => t.status === TenderStatus.WON));
        } catch (e) { console.error(e); }
    };
    loadTenders();

        // Se a URL contém ?openOrder=<id>, abrir automaticamente o modal Kanban para essa ordem
        // Ex.: http://host/#/orders?openOrder=<orderId>
        // Note: alguns links (HashRouter ou e-mails) colocam os query params depois do '#' (ex: /#/orders?openOrder=...)
        // então tentamos extrair params tanto de location.search quanto de window.location.hash para ser resiliente.
        (async () => {
            try {
                const rawSearch = (() => {
                    // Preferir location.search (React Router), cair para parsing do hash quando vazio
                    if (location && location.search && location.search.length > 0) return location.search.replace(/^\?/, '');
                    if (typeof window !== 'undefined' && window.location && window.location.hash) {
                        const idx = window.location.hash.indexOf('?');
                        if (idx !== -1) return window.location.hash.slice(idx + 1);
                    }
                    return '';
                })();

                const params = new URLSearchParams(rawSearch);
                const openOrderId = params.get('openOrder');
                const focusId = params.get('focusComment');
                if (!openOrderId) return;
                // Force Kanban view so the detail modal can render when opened via email deep-link
                setViewMode('kanban');
                if (focusId) setFocusCommentId(focusId);
                // buscar a ordem no backend (caso ainda não esteja carregada)
                const all = await apiService.getOrders();
                const found = all.find((o: any) => String(o.id) === String(openOrderId));
                if (found) {
                    setOrders((prev) => (prev.find(p => p.id === found.id) ? prev : [found, ...prev]));
                    setKanbanOrder(found as any);
                    setIsKanbanModalOpen(true);
                } else {
                    // fallback: tentar buscar a ordem individualmente (caso não esteja na listagem principal)
                    try {
                        const single = await apiService.getOrderById(openOrderId);
                        if (single) {
                            setOrders((prev) => (prev.find(p => p.id === single.id) ? prev : [single, ...prev]));
                            setKanbanOrder(single as any);
                            setIsKanbanModalOpen(true);
                        }
                    } catch (err) {
                        console.warn('Fallback: não foi possível buscar a ordem por id:', err);
                    }
                }
            } catch (e) {
                console.error('Erro ao abrir deep-link de ordem:', e);
            }
        })();
    }, []);

    // Load comments when kanban modal opens
    useEffect(() => {
        const loadComments = async () => {
            if (!isKanbanModalOpen || !kanbanOrder) {
                setKanbanComments([]);
                return;
            }
            setKanbanLoadingComments(true);
            try {
                const c = await apiService.getOrderComments(kanbanOrder.id);
                setKanbanComments(c || []);
                // If a deep-link requested focus on a comment, attempt to scroll/highlight after render
                if (focusCommentId) {
                    // small delay to allow DOM render
                    setTimeout(() => {
                        try {
                            const el = document.getElementById(`comment-${focusCommentId}`);
                            if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                // add highlight classes (Tailwind utilities) then remove after timeout
                                el.classList.add('ring-4', 'ring-yellow-300', 'bg-yellow-50 dark:bg-yellow-900/30', 'rounded-md');
                                // make focusable for accessibility
                                const hadTab = el.hasAttribute('tabindex');
                                if (!hadTab) el.setAttribute('tabindex', '-1');
                                (el as HTMLElement).focus();
                                setTimeout(() => {
                                    el.classList.remove('ring-4', 'ring-yellow-300', 'bg-yellow-50 dark:bg-yellow-900/30', 'rounded-md');
                                    if (!hadTab) el.removeAttribute('tabindex');
                                }, 4500);
                            }
                        } catch (e) { /* ignore */ }
                        // cleanup query params so reloading doesn't re-trigger
                        try {
                            const params = new URLSearchParams(location.search);
                            params.delete('focusComment');
                            params.delete('openOrder');
                            navigate({ pathname: '/orders', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
                        } catch (e) { /* ignore */ }
                        setFocusCommentId(null);
                    }, 150);
                }
            } catch (e) {
                console.error('Erro ao carregar comentários:', e);
            } finally {
                setKanbanLoadingComments(false);
            }
        };
        loadComments();
    }, [isKanbanModalOpen, kanbanOrder, focusCommentId, location.search, navigate]);

    // Description edit handlers for premium modal
    const handleStartEditDescription = () => {
        setKanbanDescriptionDraft(kanbanOrder?.description || '');
        setKanbanTitleDraft(kanbanOrder?.title || '');
        setKanbanEditingDescription(true);
    };

    const handleCancelEditDescription = () => {
        setKanbanDescriptionDraft('');
        setKanbanTitleDraft('');
        setKanbanEditingDescription(false);
    };

    const handleSaveDescription = async () => {
        if (!kanbanOrder) return;
        try {
            if (!kanbanTitleDraft || !kanbanTitleDraft.trim()) {
                await showAlert('O campo "Objeto / Título" é obrigatório.');
                return;
            }
            const payload: any = { title: kanbanTitleDraft.trim(), description: kanbanDescriptionDraft };
            await apiService.updateOrder(kanbanOrder.id, payload);
            const all = await apiService.getOrders();
            setOrders(all);
            const updated = all.find((o: any) => o.id === kanbanOrder.id) || null;
            setKanbanOrder(updated as any);
            setKanbanEditingDescription(false);
        } catch (e) {
            console.error('Erro ao salvar descrição:', e);
            await showAlert('Erro ao salvar descrição.');
        }
    };

    const handleCopyDescription = async () => {
        try {
            const text = kanbanOrder?.description || '';
            await navigator.clipboard.writeText(text);
            await showAlert('Descrição copiada para a área de transferência.');
        } catch (e) {
            console.error('Erro ao copiar descrição:', e);
            await showAlert('Não foi possível copiar.');
        }
    };

    // Meta edit handlers (supplier, documentType, requestDate)
    const handleStartEditMeta = () => {
        setKanbanMetaDraft({
            supplier: kanbanOrder?.supplier || '',
            documentType: kanbanOrder?.documentType || 'EMPENHO',
            requestDate: kanbanOrder?.requestDate ? (kanbanOrder.requestDate.split('T')[0]) : ''
        });
        setKanbanEditingMeta(true);
    };

    const handleCancelEditMeta = () => {
        setKanbanMetaDraft({ supplier: '', documentType: 'EMPENHO', requestDate: '' });
        setKanbanEditingMeta(false);
    };

    const handleSaveMeta = async () => {
        if (!kanbanOrder) return;
        try {
            setKanbanSavingMeta(true);
            const payload: any = {
                supplier: kanbanMetaDraft.supplier || null,
                documentType: kanbanMetaDraft.documentType || null,
                requestDate: kanbanMetaDraft.requestDate || null,
            };
            await apiService.updateOrder(kanbanOrder.id, payload);
            const all = await apiService.getOrders();
            setOrders(all);
            const updated = all.find((o: any) => o.id === kanbanOrder.id) || null;
            setKanbanOrder(updated as any);
            setKanbanEditingMeta(false);
        } catch (e) {
            console.error('Erro ao salvar metadados:', e);
            await showAlert('Erro ao salvar informações do pedido.');
        } finally {
            setKanbanSavingMeta(false);
        }
    };

    // Reusable uploader for attachments (used by drop and file input)
    const uploadFilesForOrder = async (files: File[], orderId: string) => {
        let success = 0; let failed = 0;
        for (const f of files) {
            if (!['application/pdf', 'text/html', 'image/jpeg', 'image/png'].includes(f.type)) { failed++; continue; }
            try {
                const companyIdForKey = kanbanOrder?.companyId || '';
                const fileExt = f.name.split('.').pop();
                const key = `orders/${companyIdForKey}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
                const { uploadUrl } = await apiService.getPresignedUploadUrl(key, f.type);
                await apiService.uploadFileToS3(uploadUrl, f);
                await apiService.createOrderAttachment(orderId, { name: f.name, url: key, type: f.type, size: f.size });
                success++;
            } catch (err) { console.error('Erro ao enviar anexo:', err); failed++; }
        }
        try { const all = await apiService.getOrders(); setOrders(all); const updated = all.find((o:any)=>o.id===orderId) || null; setKanbanOrder(updated as any); await showAlert(`Upload: ${success} sucesso(s), ${failed} falha(s)`); } catch (err) { console.error(err); }
    };

    const handleDropAttachments = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault(); setKanbanDropHover(false);
        const files = Array.from(e.dataTransfer.files || []) as File[];
        if (files.length === 0 || !kanbanOrder) return;
        await uploadFilesForOrder(files, kanbanOrder.id);
    };

    const handleSelectAttachments = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) as File[] : [];
        if (files.length === 0 || !kanbanOrder) return;
        await uploadFilesForOrder(files, kanbanOrder.id);
        // clear input
        (e.target as HTMLInputElement).value = '';
    };

    // --- Comment media helpers ---
    const addCommentFiles = (incoming: File[]) => {
        // Update files list (no side effects inside updater to avoid double-run in StrictMode)
        setCommentFiles(prev => [...prev, ...incoming]);

        // Build previews separately, outside the state updater
        incoming.forEach(f => {
            const isImage = f.type.startsWith('image/');
            if (isImage) {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    setCommentPreviews(p => [...p, { name: f.name, url: ev.target?.result as string, isImage: true }]);
                };
                reader.readAsDataURL(f);
            } else {
                setCommentPreviews(p => [...p, { name: f.name, url: '', isImage: false }]);
            }
        });
    };

    const removeCommentFile = (idx: number) => {
        setCommentFiles(prev => prev.filter((_, i) => i !== idx));
        setCommentPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const handleCommentFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) as File[] : [];
        if (files.length > 0) addCommentFiles(files);
        (e.target as HTMLInputElement).value = '';
    };

    const handleCommentPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = Array.from(e.clipboardData.items) as DataTransferItem[];
        const fileItems = items.filter(it => it.kind === 'file').map(it => it.getAsFile()).filter((f): f is File => f !== null);
        if (fileItems.length > 0) {
            e.preventDefault();
            addCommentFiles(fileItems);
        }
    };

    const handleCommentDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files || []) as File[];
        if (files.length > 0) addCommentFiles(files);
    };

    const clearCommentAttachments = () => {
        setCommentFiles([]);
        setCommentPreviews([]);
    };

    // --- Audio recording helpers ---
    const formatAudioDuration = (secs: number) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioStreamRef.current = stream;
            audioChunksRef.current = [];
            setAudioDuration(0);

            // prefer webm/opus, fall back to whatever the browser supports
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : '';
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            mediaRecorderRef.current = mr;

            mr.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioPreviewUrl(url);
                // stop all tracks
                stream.getTracks().forEach(t => t.stop());
                audioStreamRef.current = null;
            };

            mr.start(200); // collect every 200ms
            setAudioRecording(true);

            // tick timer
            audioTimerRef.current = setInterval(() => {
                setAudioDuration(d => d + 1);
            }, 1000);
        } catch (err) {
            console.error('Erro ao acessar microfone:', err);
            await showAlert('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
        }
    };

    const stopRecording = () => {
        if (audioTimerRef.current) { clearInterval(audioTimerRef.current); audioTimerRef.current = null; }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setAudioRecording(false);
    };

    const cancelRecording = () => {
        if (audioTimerRef.current) { clearInterval(audioTimerRef.current); audioTimerRef.current = null; }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        audioStreamRef.current?.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;
        setAudioRecording(false);
        setAudioBlob(null);
        if (audioPreviewUrl) { URL.revokeObjectURL(audioPreviewUrl); }
        setAudioPreviewUrl(null);
        setAudioDuration(0);
        audioChunksRef.current = [];
    };

    const discardAudio = () => {
        setAudioBlob(null);
        if (audioPreviewUrl) { URL.revokeObjectURL(audioPreviewUrl); }
        setAudioPreviewUrl(null);
        setAudioDuration(0);
    };

    // Send the recorded audio: upload to S3 and publish comment
    const sendAudioComment = async () => {
        if (!audioBlob || !kanbanOrder) return;
        setKanbanPostingComment(true);
        try {
            const companyId = kanbanOrder.companyId || '';
            const ext = audioBlob.type.includes('ogg') ? 'ogg' : 'webm';
            const fileName = `audio-${Date.now()}.${ext}`;
            const key = `orders/${companyId}/comments/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${ext}`;
            const { uploadUrl } = await apiService.getPresignedUploadUrl(key, audioBlob.type || 'audio/webm');
            await apiService.uploadFileToS3(uploadUrl, new File([audioBlob], fileName, { type: audioBlob.type }));
            const userRaw = localStorage.getItem('user');
            const user = userRaw ? JSON.parse(userRaw) : null;
            await apiService.createOrderComment(kanbanOrder.id, {
                content: '',
                userId: user?.id,
                userName: user?.name,
                meta: { attachments: [{ name: fileName, url: key, type: audioBlob.type || 'audio/webm', size: audioBlob.size }] },
            });
            discardAudio();
            setKanbanComments(await apiService.getOrderComments(kanbanOrder.id));
        } catch (err) {
            console.error('Erro ao enviar áudio:', err);
            await showAlert('Erro ao enviar áudio. Tente novamente.');
        } finally {
            setKanbanPostingComment(false);
        }
    };

    // Upload comment files to S3 and return attachment metadata list
    const uploadCommentFiles = async (orderId: string): Promise<{ name: string; url: string; type: string; size: number }[]> => {
        const companyId = kanbanOrder?.companyId || '';
        const results: { name: string; url: string; type: string; size: number }[] = [];
        for (const f of commentFiles) {
            try {
                const fileExt = f.name.split('.').pop();
                const key = `orders/${companyId}/comments/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
                const { uploadUrl } = await apiService.getPresignedUploadUrl(key, f.type);
                await apiService.uploadFileToS3(uploadUrl, f);
                results.push({ name: f.name, url: key, type: f.type, size: f.size });
            } catch (err) {
                console.error('Erro ao enviar arquivo de comentário:', err);
            }
        }
        return results;
    };

    // Limpar filtros da tela de Empenhos & O.S.
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('ALL');
        setDocTypeFilter('ALL');
        setCompanyFilter('ALL');
        setDeadlineStart('');
        setDeadlineEnd('');
        setUrgencyFilter('ALL');
    };



  // Helper para dias restantes
  const getDaysRemaining = (dateString?: string) => {
    if (!dateString) return null;
    return daysUntilCalendarDate(dateString);
  };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files) as File[];
      // If not editing, keep the previous behavior: store the first file as the main selectedFile
            const firstFile: File = files[0];
      // Aceita PDF, HTML e Imagens para anexo manual
      // validate type for first file (we'll validate all below)
      if (!['application/pdf', 'text/html', 'image/jpeg', 'image/png'].includes(firstFile.type)) {
          await showAlert('Apenas arquivos PDF, HTML ou Imagens são permitidos.');
          return;
      }
    setSelectedFile(firstFile);
    setAiSuccess(false); // Reseta IA se mudar arquivo manualmente
    // If creating (no editingOrder), store full selection so we can upload after create
    if (!editingOrder) setSelectedFiles(files);

      if (!formData.title) {
          const nameWithoutExt = firstFile.name.replace(/\.(pdf|html|jpg|png|jpeg)$/i, '');
          setFormData(prev => ({ ...prev, title: nameWithoutExt }));
      }

      // If we're editing an existing order, upload all selected files as complementary attachments
      if (editingOrder) {
          let success = 0;
          let failed = 0;
          for (const file of files) {
              if (!['application/pdf', 'text/html', 'image/jpeg', 'image/png'].includes(file.type)) {
                  failed++;
                  continue;
              }
              try {
                  const companyIdForKey = editingOrder.companyId || formData.companyId || '';
                  const fileExt = file.name.split('.').pop();
                  const key = `orders/${companyIdForKey}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
                  const { uploadUrl } = await apiService.getPresignedUploadUrl(key, file.type);
                  await apiService.uploadFileToS3(uploadUrl, file);
                  // create attachment record
                  await apiService.createOrderAttachment(editingOrder.id, {
                      name: file.name,
                      url: key,
                      type: file.type,
                      size: file.size,
                  });
                  success++;
              } catch (err) {
                  console.error('Erro ao enviar anexo:', err);
                  failed++;
              }
          }
          try {
              // refresh orders and the editingOrder attachments
              const all = await apiService.getOrders();
              setOrders(all);
              const updated = all.find(o => o.id === editingOrder.id) || null;
              setEditingOrder(updated as any);
              setSelectedFile(null);
              await showAlert(`Upload concluído: ${success} adicionados, ${failed} falharam.`);
          } catch (err) {
              console.error('Erro ao atualizar lista após upload:', err);
              await showAlert('Upload concluído, porém falha ao atualizar a lista de anexos.');
          }
      }
    }
  };

  // --- FUNÇÃO MÁGICA: Extração com IA ---
  const handleAiExtraction = async (file: File) => {
            setIsExtracting(true);
            try {
                const extracted = await apiService.extractOrderFromPDF(file);

                const parseDDMMYYYY = (s?: string | null) => {
                    if (!s || s === 'NÃO CONSTA') return undefined;
                    const parts = s.split('/').map(p => p.trim());
                    if (parts.length !== 3) return undefined;
                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                };

                const parseCurrencyToNumber = (v?: any) => {
                    if (!v || v === 'NÃO CONSTA') return 0;
                    if (typeof v === 'number') return v;
                    try {
                        const cleaned = String(v).replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(/,/g, '.');
                        const n = parseFloat(cleaned);
                        return isNaN(n) ? 0 : n;
                    } catch (e) { return 0; }
                };

                const mapDocumentType = (dt?: string) => {
                    if (!dt) return 'OUTRO';
                    const d = dt.toLowerCase();
                    if (d.includes('empenho')) return 'EMPENHO';
                    if (d.includes('ordem') || d.includes('o.s')) return 'ORDEM_SERVICO';
                    if (d.includes('autoriz')) return 'AUTORIZACAO';
                    return 'OUTRO';
                };

                const inferStatus = (parsedStatus?: string, deadlineIso?: string, evidence?: string) => {
                    const now = new Date();
                    if (parsedStatus) {
                        const s = parsedStatus.toLowerCase();
                        if (s.includes('venc')) return 'OVERDUE';
                        if (s.includes('entreg') || s.includes('receb')) return 'DELIVERED';
                        if (s.includes('exec') || s.includes('execução')) return 'IN_PROGRESS';
                        if (s.includes('pend')) return 'PENDING';
                        if (s.includes('prazo')) return 'OPEN';
                    }
                    if (deadlineIso && deadlineIso !== 'NÃO CONSTA') {
                        const d = new Date(deadlineIso);
                        if (!isNaN(d.getTime())) {
                            if (d < now && !(evidence && /receb/i.test(evidence))) return 'OVERDUE';
                            if (d >= now) return 'PENDING';
                        }
                    }
                    return 'PENDING';
                };

                if (extracted) {
                    const issueIso = parseDDMMYYYY(extracted.issue_date);
                    // If deadline_text contains days, compute
                    let deadlineIso = parseDDMMYYYY(extracted.deadline_date);
                    if ((!deadlineIso || deadlineIso === undefined) && extracted.deadline_text && /\d+\s*dias?/i.test(extracted.deadline_text)) {
                        const m = extracted.deadline_text.match(/(\d+)\s*dias?/i);
                        if (m && issueIso) {
                            const issue = new Date(issueIso + 'T00:00:00Z');
                            issue.setDate(issue.getDate() + parseInt(m[1], 10));
                            deadlineIso = issue.toISOString().split('T')[0];
                        }
                    }

                    const status = inferStatus(extracted.status, deadlineIso, extracted.evidence || extracted.document_analysis);

                    setFormData(prev => ({
                        ...prev,
                        number: extracted.number && extracted.number !== 'NÃO CONSTA' ? extracted.number : prev.number,
                        title: extracted.object && extracted.object !== 'NÃO CONSTA' ? extracted.object : prev.title || file.name,
                        supplier: extracted.client && extracted.client !== 'NÃO CONSTA' ? extracted.client : prev.supplier,
                        value: parseCurrencyToNumber(extracted.value) || prev.value,
                        requestDate: issueIso || prev.requestDate || new Date().toISOString().split('T')[0],
                        deliveryDate: deadlineIso || prev.deliveryDate || '',
                        type: prev.type || 'PRODUCT',
                        documentType: mapDocumentType(extracted.document_type) as any,
                        description: `Importado via IA: ${extracted.object || extracted.document_analysis || ''}`
                    }));

                    setSelectedFile(file);
                    setAiSuccess(true);
                    await showAlert('Dados extraídos com sucesso! Verifique os campos preenchidos.');
                    logActivity('EMPENHO_EXTRAIDO_IA', `Empenho/OS extraído via IA: ${extracted.number || extracted.evidence || ''}`, 'SUCCESS');
                } else {
                    await showAlert('Não foi possível extrair dados do documento. Tente preencher manualmente.');
                }
            } catch (err) {
                console.error('Erro na extração de ordem:', err);
                await showAlert('Não foi possível extrair dados do documento. Tente preencher manualmente.');
            } finally {
                setIsExtracting(false);
            }
  };

  const removeSelectedFile = (index: number) => {
      setSelectedFiles(prev => {
          const next = prev.filter((_, i) => i !== index);
          setSelectedFile(next[0] || null);
          return next;
      });
  };

    const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          const file = e.dataTransfer.files[0];
          const validTypes = ['application/pdf', 'text/html', 'image/jpeg', 'image/png'];

          if (validTypes.includes(file.type)) {
              handleAiExtraction(file); // Gatilho automático ao soltar
          } else {
              await showAlert("Formato não suportado para leitura inteligente. Use PDF, HTML ou Imagem.");
          }
      }
  };

  const handleTenderSelection = (tenderId: string) => {
      const tender = wonTenders.find(t => t.id === tenderId);
      if (tender) {
          setFormData(prev => ({
              ...prev,
              tenderId: tender.id,
              supplier: tender.organ, // Define o Órgão como "Supplier/Emissor"
              title: prev.title || `Empenho - ${tender.title}`, // Sugere título
              value: prev.value || tender.value // Sugere valor
          }));
      } else {
          setFormData(prev => ({ ...prev, tenderId: '' }));
      }
  };

  const handleOpenModal = (order?: PurchaseOrder) => {
    let defaultCompanyId = '';
    if (companies.length > 0) {
        const defaultComp = companies.find(c => c.isDefault);
        defaultCompanyId = defaultComp ? defaultComp.id : companies[0].id;
    }

        if (order) {
            setEditingOrder(order);
            // Normalize incoming order to ensure form fields comply with inputs
            setFormData({
                ...order,
                requestDate: order.requestDate ? order.requestDate.split('T')[0] : '',
                deliveryDate: order.deliveryDate ? order.deliveryDate.split('T')[0] : '',
                tenderId: order.tenderId || '',
                documentType: order.documentType || 'EMPENHO',
                status: order.status || 'PENDING',
                companyId: order.companyId || defaultCompanyId,
                description: order.description || '',
                supplier: order.supplier || '',
                title: order.title || '',
                number: order.number || '',
                type: order.type || 'PRODUCT',
                value: order.value || 0
            });
            setSelectedFile(null);
    } else {
      setEditingOrder(null);
      setFormData({
        number: '',
        title: '',
        type: 'PRODUCT',
        documentType: 'EMPENHO',
        supplier: '',
        tenderId: '',
        requestDate: new Date().toISOString().split('T')[0],
        value: 0,
        status: 'PENDING',
        description: '',
        companyId: defaultCompanyId
      });
      setSelectedFile(null);
    }
    setAiSuccess(false);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
        if (!formData.title || !formData.supplier || !formData.number) {
            await showAlert("Campos obrigatórios: Número, Título/Objeto e Órgão Emissor.");
            return;
        }
    try {
        // Ensure we have a valid companyId before sending to API (avoid FK P2003)
        const finalCompanyId = formData.companyId || (companies && companies.length > 0 ? companies[0].id : undefined);
        if (!finalCompanyId) {
            await showAlert('Selecione uma empresa válida antes de salvar.');
            return;
        }

        // Normalize companyId into payload (we don't rely on the async setFormData to complete)
        setFormData(prev => ({ ...prev, companyId: finalCompanyId }));

        // Build payload from current formData but ensure required FKs are valid.
        const payload = { ...formData, companyId: formData.companyId || finalCompanyId };

        // Sanitize payload: don't send empty-string foreign keys (backend expects null/omitted or a valid id)
        const sanitizedPayload: any = { ...payload };
        if (sanitizedPayload.tenderId === '' || sanitizedPayload.tenderId === null) {
            // Omit tenderId when not set to avoid FK violation on backend
            delete sanitizedPayload.tenderId;
        }

        // First create or update the order
        let savedOrder: any = null;
        if (editingOrder) {
            // Log sanitized payload to help debug FK errors returned by the API
            console.info('Updating order', { id: editingOrder.id, payload: sanitizedPayload });
            try {
                // Ensure we don't send the 'id' in the body (some backends reject it)
                const toSend = { ...sanitizedPayload };
                delete toSend.id;
                // Log the actual payload that will be sent
                console.info('Sending updateOrder payload (toSend)', toSend);
                const apiResp = await apiService.updateOrder(editingOrder.id, toSend);
                console.info('updateOrder API response:', apiResp);
                savedOrder = apiResp;
                logActivity('EMPENHO_ATUALIZACAO', `Empenho/OS ${formData.number} atualizado`, 'INFO');
            } catch (err: any) {
                // If the apiService attached response data, log it for diagnosis and show a clearer alert
                console.error('API updateOrder error', err?.response || err);
                const details = err?.response?.data ? JSON.stringify(err.response.data, null, 2) : err?.message || String(err);
                await showAlert('Erro ao atualizar ordem: ' + details);
                throw err; // rethrow so calling flow still knows it failed
            }
        } else {
            // For create we also avoid sending empty FK fields
            const createPayload = { ...sanitizedPayload };
            if (createPayload.tenderId === '' || createPayload.tenderId == null) delete createPayload.tenderId;
            console.info('Creating order', { payload: createPayload });
            savedOrder = await apiService.createOrder(createPayload);
            logActivity('EMPENHO_CRIACAO', `Novo Empenho/OS ${formData.number} registrado`, 'SUCCESS');
        }

        // If pre-selected multiple files exist (create flow), upload them as complementary attachments
        // Skip the first file if it was already uploaded as the main fileUrl (selectedFile)
        const filesToAttach = (selectedFiles && selectedFiles.length > 0)
            ? (selectedFile ? selectedFiles.slice(1) : selectedFiles)
            : [];
        if (filesToAttach.length > 0) {
            for (const f of filesToAttach) {
                try {
                    const fileExt = f.name.split('.').pop();
                    const key = `orders/${finalCompanyId}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
                    const { uploadUrl } = await apiService.getPresignedUploadUrl(key, f.type);
                    await apiService.uploadFileToS3(uploadUrl, f);
                    try {
                        await apiService.createOrderAttachment(savedOrder.id, {
                            name: f.name,
                            url: key,
                            type: f.type,
                            size: f.size,
                        });
                    } catch (e) {
                        console.warn('Ordem salva, mas falha ao registrar anexo:', e);
                    }
                } catch (err) {
                    console.error('Falha ao enviar anexo pre-save:', err);
                }
            }
            // clear pre-selected files after upload
            setSelectedFiles([]);
        } else if (selectedFile) {
            // backward-compat single file: the file was already uploaded as fileUrl above, no need to duplicate as attachment
        }

        setOrders(await apiService.getOrders());
        setIsModalOpen(false);
    } catch (error: any) {
        console.error(error);
        const details = error?.response?.data ? JSON.stringify(error.response.data, null, 2) : error?.message || String(error);
        await showAlert('Erro ao salvar ordem: ' + details);
    }
  };

  // Deletar anexo da ordem (UI)
  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!editingOrder) return;
    const ok = await showConfirm('Deseja remover este anexo?');
    if (!ok) return;
    try {
      await apiService.deleteOrderAttachment(editingOrder.id, attachmentId);
      setOrders(await apiService.getOrders());
      // refresh editingOrder in modal
      const updated = (await apiService.getOrders()).find((o: any) => o.id === editingOrder.id);
      setEditingOrder(updated || null);
    } catch (e) {
      console.error(e);
      await showAlert('Erro ao remover anexo.');
    }
  };

    const handleDelete = async (id: string, number: string) => {
        const ok = await showConfirm(`Excluir registro ${number}?`);
        if (!ok) return;
        try {
                await apiService.deleteOrder(id);
                setOrders(await apiService.getOrders());
                logActivity('EMPENHO_EXCLUSAO', `Registro ${number} excluído`, 'WARNING');
        } catch (error) {
                console.error(error);
                await showAlert("Erro ao excluir ordem.");
        }
    };

  const handleDownload = async (order: PurchaseOrder) => {
      try {
        if (order.fileUrl) {
            const { downloadUrl } = await apiService.getPresignedDownloadUrl(order.fileUrl);
            window.open(downloadUrl, '_blank');
            return;
        }

        if (order.file && order.file.base64) {
            const link = document.createElement('a');
            link.href = `data:${order.file.type};base64,${order.file.base64}`;
            link.download = order.file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }
                await showAlert('Nenhum arquivo anexado.');
      } catch (e) {
          console.error(e);
                    await showAlert('Erro ao baixar arquivo.');
      }
  };

  const getStatusBadge = (status: OrderStatus) => {
      switch (status) {
          case 'OPEN': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/60"><CheckCircle className="w-3 h-3"/> No prazo</span>;
          case 'OVERDUE': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/60"><AlertCircle className="w-3 h-3"/> Vencido</span>;
          case 'DELIVERED': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark"><CheckCircle className="w-3 h-3"/> Entregue</span>;
          case 'APPROVED': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/60"><CheckCircle className="w-3 h-3"/> Aprovado</span>;
          case 'PENDING': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:text-yellow-300 border border-yellow-200"><Clock className="w-3 h-3"/> Pendente</span>;
          case 'IN_PROGRESS': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60"><Package className="w-3 h-3"/> Em Execução</span>;
          case 'COMPLETED': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark"><CheckCircle className="w-3 h-3"/> Entregue/Finalizado</span>;
          case 'CANCELLED': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/60"><XCircle className="w-3 h-3"/> Cancelado</span>;
      }
  };

  const getDocTypeBadge = (type?: OrderDocumentType) => {
      switch(type) {
          case 'EMPENHO': return <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200 uppercase">Nota de Empenho</span>;
          case 'ORDEM_SERVICO': return <span className="text-[10px] font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded border border-orange-200 dark:border-orange-800/60 uppercase">Ordem de Serviço</span>;
          case 'AUTORIZACAO': return <span className="text-[10px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-800/60 uppercase">Autorização</span>;
          default: return <span className="text-[10px] font-bold bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark px-2 py-0.5 rounded border border-line dark:border-line-dark uppercase">Outro</span>;
      }
  };

  const filteredOrders = orders.filter(o => {
      const matchesSearch = o.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            o.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            o.supplier.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || o.status === statusFilter;
      const matchesDocType = docTypeFilter === 'ALL' || o.documentType === docTypeFilter;
      const matchesCompany = companyFilter === 'ALL' || o.companyId === companyFilter;

      // Filtro de Data de Execução (Range)
      let matchesDate = true;
      if (deadlineStart) {
          matchesDate = matchesDate && !!o.deliveryDate && o.deliveryDate >= deadlineStart;
      }
      if (deadlineEnd) {
          matchesDate = matchesDate && !!o.deliveryDate && o.deliveryDate <= deadlineEnd;
      }

      // Filtro de Urgência (Dias Restantes)
      let matchesUrgency = true;
      if (urgencyFilter !== 'ALL') {
          const days = getDaysRemaining(o.deliveryDate);
          if (days === null) {
              matchesUrgency = false;
          } else {
              if (urgencyFilter === 'EXPIRED') matchesUrgency = days < 0;
              else if (urgencyFilter === 'URGENT') matchesUrgency = days >= 0 && days <= 7;
              else if (urgencyFilter === 'WARNING') matchesUrgency = days > 7 && days <= 30;
          }
      }

      return matchesSearch && matchesStatus && matchesDocType && matchesDate && matchesUrgency && matchesCompany;
  });

        return (
            <div className="w-full max-w-full overflow-x-hidden"> {/* page: prevents body horizontal scroll */}
                <div className="w-full">
                    <div className="space-y-6 pb-8">
      {/* Header */}

                    <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-4 md:p-6 flex-shrink-0">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                            <Gavel className="w-5 h-5 text-brand-700 dark:text-brand-300" />
                            Gestão de Empenhos e O.S.
                        </h2>
                        <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Gerencie notas de empenho e ordens de serviço recebidas de licitações ganhas.</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => handleOpenModal()}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Novo Registro
                        </button>
                        <ClearFiltersButton onClear={clearFilters} />
                    </div>
                </div>
        {/* Filtros Principais — padrão unificado com Licitações */}
        <div className="mt-6 flex flex-col gap-3">
            {/* Linha 1: Busca grande + contador + mode switcher */}
            <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar por número, órgão ou objeto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-10 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[13.5px] rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 block pl-10 pr-3 outline-none transition-colors"
                    />
                </div>
                <span className="hidden md:inline-flex items-center text-[11.5px] text-ink-subtitle dark:text-ink-subtitle-dark whitespace-nowrap tabular px-2">
                    {filteredOrders.length} de {orders.length}
                </span>
                <div className="inline-flex items-center gap-0.5 p-1 rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark flex-shrink-0" role="radiogroup" aria-label="Modo de visualização">
                    {([
                        { k: 'list' as const,   label: 'Lista',  Icon: List },
                        { k: 'cards' as const,  label: 'Cards',  Icon: CheckSquare },
                        { k: 'kanban' as const, label: 'Quadro', Icon: Activity },
                    ]).map(o => {
                        const active = viewMode === o.k;
                        return (
                            <button
                                key={o.k}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                title={o.label}
                                onClick={() => setViewMode(o.k)}
                                className={[
                                    'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium transition-colors',
                                    active
                                        ? 'bg-brand-500 text-white'
                                        : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark',
                                ].join(' ')}
                            >
                                <o.Icon className="w-3 h-3" />
                                <span className="hidden lg:inline">{o.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Linha 2: Chips rápidos — PRAZO + STATUS */}
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5">
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Prazo</span>
                {([
                    { k: 'ALL' as const,  label: 'Todos' },
                    { k: 'HOJE' as const, label: 'Hoje' },
                    { k: '7D' as const,   label: 'Próximos 7 dias' },
                    { k: '30D' as const,  label: '30 dias' },
                ]).map(c => {
                    const active = quickPrazo === c.k;
                    return (
                        <button
                            key={c.k}
                            type="button"
                            onClick={() => setQuickPrazo(c.k)}
                            className={[
                                'inline-flex items-center h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                                active
                                    ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                                    : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                            ].join(' ')}
                        >
                            {c.label}
                        </button>
                    );
                })}

                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark ml-1">Status</span>
                {([
                    { k: 'VENCIDO' as const,  label: 'Vencido',     dot: 'bg-danger dark:bg-danger-dark' },
                    { k: 'PENDENTE' as const, label: 'Pendente',    dot: 'bg-warning dark:bg-warning-dark' },
                    { k: 'EXEC' as const,     label: 'Em execução', dot: 'bg-info dark:bg-info-dark' },
                    { k: 'COMPLETO' as const, label: 'Entregue',    dot: 'bg-success dark:bg-success-dark' },
                ]).map(c => {
                    const active = quickStatus === c.k;
                    return (
                        <button
                            key={c.k}
                            type="button"
                            onClick={() => setQuickStatus(active ? 'ALL' : c.k)}
                            className={[
                                'inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                                active
                                    ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                                    : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                            ].join(' ')}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                            {c.label}
                        </button>
                    );
                })}

                {/* Separador visual — empurra os dropdowns pra direita */}
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <div className="relative w-44">
                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                            <Briefcase className="h-3.5 w-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                        </div>
                        <select
                            value={companyFilter}
                            onChange={(e) => setCompanyFilter(e.target.value)}
                            className="w-full h-7 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[12px] rounded-full focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 block pl-8 pr-6 outline-none cursor-pointer appearance-none"
                        >
                            <option value="ALL">Empresa: Todas</option>
                            {companies.map(comp => (
                                <option key={comp.id} value={comp.id}>{comp.tradeName}</option>
                            ))}
                        </select>
                    </div>
                    <select
                        value={docTypeFilter}
                        onChange={(e) => setDocTypeFilter(e.target.value as any)}
                        className="h-7 pl-3 pr-6 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[12px] rounded-full focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none cursor-pointer appearance-none"
                    >
                        <option value="ALL">Tipo Doc: Todos</option>
                        <option value="EMPENHO">Nota de Empenho</option>
                        <option value="ORDEM_SERVICO">Ordem de Serviço</option>
                        <option value="AUTORIZACAO">Autorização</option>
                    </select>
                </div>
            </div>

            {/* Linha 2: Filtros de Prazo */}
            <div className="flex flex-col md:flex-row gap-4 bg-bg dark:bg-bg-dark p-3 rounded-lg border border-line dark:border-line-dark items-start md:items-center flex-wrap">
                <div className="flex items-center gap-2 text-sm text-ink-label dark:text-ink-label-dark font-medium min-w-fit">
                    <Calendar className="w-4 h-4 text-brand-700 dark:text-brand-300" />
                    Prazo de Execução:
                </div>

                <div className="flex items-center gap-2 flex-grow flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">De:</span>
                        <input
                            type="date"
                            value={deadlineStart}
                            onChange={(e) => setDeadlineStart(e.target.value)}
                            className="bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark text-xs rounded-md p-1.5 focus:ring-brand-500 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Até:</span>
                        <input
                            type="date"
                            value={deadlineEnd}
                            onChange={(e) => setDeadlineEnd(e.target.value)}
                            className="bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark text-xs rounded-md p-1.5 focus:ring-brand-500 outline-none"
                        />
                    </div>
                </div>

                <div className="w-full md:w-64">
                    <select
                        value={urgencyFilter}
                        onChange={(e) => setUrgencyFilter(e.target.value as any)}
                        className={`w-full px-3 py-1.5 border text-sm rounded-md outline-none cursor-pointer font-medium ${
                            urgencyFilter === 'EXPIRED' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/60' :
                            urgencyFilter === 'URGENT' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800/60' :
                            'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line-strong dark:border-line-dark-strong'
                        }`}
                    >
                        <option value="ALL">Prazo Restante: Todos</option>
                        <option value="URGENT">🚨 Urgente (Próx. 7 dias)</option>
                        <option value="WARNING">⚠️ Atenção (Próx. 30 dias)</option>
                        <option value="EXPIRED">❌ Vencidos</option>
                    </select>
                </div>
            </div>
        </div>
      </div>

      {/* Lista ou Kanban (ambas sincronizadas usando o mesmo 'orders') */}
                        {viewMode === 'kanban' ? (
                            <section className="board bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark w-full">
                                {/* board-scroll: horizontal scroll container */}
                                <div className="w-full max-w-full overflow-x-auto">
                                    <OrderKanban
                                            orders={filteredOrders}
                                            onCardDoubleClick={(o) => {
                                                // open the trello-like detail modal only when in kanban
                                                setKanbanOrder(o);
                                                setIsKanbanModalOpen(true);
                                            }}
                                            onStatusChange={handleStatusChange}
                                        />
                                </div>

                                        {/* Premium Kanban detail modal (enterprise styling) */}
                                        {isKanbanModalOpen && kanbanOrder && (
                                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                                                <button type="button" className="absolute inset-0 bg-black/50" aria-label="Fechar detalhe do pedido" onClick={() => { setIsKanbanModalOpen(false); setKanbanOrder(null); }} />

                                                <div className="relative bg-surface dark:bg-surface-dark rounded-2xl border border-[#eaeaea] shadow-lg w-full max-w-[960px] max-h-[85vh] flex flex-col overflow-hidden">
                                                    {/* Header - fixed */}
                                                    <div className="sticky top-0 z-20 bg-surface dark:bg-surface-dark border-b border-[#eaeaea] px-6 py-4 flex items-start justify-between gap-4">
                                                        <div className="min-w-0">
                                                            <h3 className="text-lg md:text-xl font-semibold text-ink-title dark:text-ink-title-dark">Pedido #{kanbanOrder.number}</h3>
                                                            {!kanbanEditingMeta ? (
                                                                <div
                                                                    className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark truncate"
                                                                    onDoubleClick={handleStartEditMeta}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleStartEditMeta(); }}
                                                                    title="Duplo clique para editar"
                                                                >
                                                                    {kanbanOrder?.supplier} • {kanbanOrder?.documentType} • {kanbanOrder?.requestDate ? formatCalendarDate(kanbanOrder.requestDate) : ''}
                                                                    <span className="sr-only">Duplo clique para editar</span>
                                                                </div>
                                                            ) : (
                                                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                                                    <input
                                                                        type="text"
                                                                        value={kanbanMetaDraft.supplier}
                                                                        onChange={(e) => setKanbanMetaDraft({ ...kanbanMetaDraft, supplier: e.target.value })}
                                                                        className="border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm"
                                                                        placeholder="Fornecedor"
                                                                    />
                                                                    <select
                                                                        value={kanbanMetaDraft.documentType}
                                                                        onChange={(e) => setKanbanMetaDraft({ ...kanbanMetaDraft, documentType: e.target.value })}
                                                                        className="border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm"
                                                                    >
                                                                        <option value="EMPENHO">Nota de Empenho</option>
                                                                        <option value="ORDEM_SERVICO">Ordem de Serviço</option>
                                                                        <option value="AUTORIZACAO">Autorização</option>
                                                                        <option value="OUTRO">Outro</option>
                                                                    </select>
                                                                    <input
                                                                        type="date"
                                                                        value={kanbanMetaDraft.requestDate || ''}
                                                                        onChange={(e) => setKanbanMetaDraft({ ...kanbanMetaDraft, requestDate: e.target.value })}
                                                                        className="border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm"
                                                                    />
                                                                    <div className="flex items-center gap-2">
                                                                        <button onClick={async () => { await handleSaveMeta(); }} disabled={kanbanSavingMeta} className="text-xs px-2 py-1 bg-brand-500 text-white hover:bg-brand-600 rounded">{kanbanSavingMeta ? 'Salvando...' : 'Salvar'}</button>
                                                                        <button onClick={handleCancelEditMeta} className="text-xs px-2 py-1 bg-surface dark:bg-surface-dark border rounded">Cancelar</button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${kanbanOrder.status === 'OVERDUE' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-100 dark:border-red-800/50' : kanbanOrder.status === 'PENDING' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-100' : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-100'}`}>
                                                                {kanbanOrder.status}
                                                            </span>
                                                            <button onClick={() => { setIsKanbanModalOpen(false); setKanbanOrder(null); }} aria-label="Fechar" className="text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark p-2 rounded-md">✕</button>
                                                        </div>
                                                    </div>

                                                    {/* Body - scrollable */}
                                                    <div className="p-6 overflow-auto flex-1">
                                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                                            {/* Left column (primary) */}
                                                            <div className="md:col-span-7 space-y-6">
                                                                {/* Description card */}
                                                                <div className="bg-surface dark:bg-surface-dark border border-[#eaeaea] rounded-lg p-4 shadow-sm">
                                                                    <div className="flex items-start justify-between gap-4">
                                                                        <div className="min-w-0">
                                                                            <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Objeto / Título *</div>
                                                                            {!kanbanEditingDescription ? (
                                                                                <div
                                                                                    onClick={() => handleStartEditDescription()}
                                                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'F2') handleStartEditDescription(); }}
                                                                                    role="button"
                                                                                    tabIndex={0}
                                                                                    className="mt-2 text-sm text-ink-title dark:text-ink-title-dark whitespace-pre-wrap cursor-pointer"
                                                                                >
                                                                                    {kanbanOrder.title || <span className="text-ink-placeholder dark:text-ink-placeholder-dark">— Sem objeto/título —</span>}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="space-y-3">
                                                                                    <div>

                                                                                        <textarea
                                                                                            id="kanbanTitle"
                                                                                            ref={kanbanTitleRef}
                                                                                            value={kanbanTitleDraft}
                                                                                            onChange={(e) => setKanbanTitleDraft(e.target.value)}
                                                                                            onInput={adjustKanbanTitleHeight}
                                                                                            className="w-[600px] border rounded p-2 text-sm md:text-base resize-none overflow-hidden max-w-full max-h-[260px]"
                                                                                            placeholder="Objeto / Título"
                                                                                            rows={2}
                                                                                        />
                                                                                    </div>

                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* buttons moved below to give more space; no inline actions here */}
                                                                    </div>
                                                                </div>

                                                                {/* Edit actions (outside the description card) */}
                                                                {kanbanEditingDescription ? (
                                                                    <div className="flex justify-end gap-3 mt-3">
                                                                        <button onClick={() => handleCancelEditDescription()} className="text-sm px-4 py-2 bg-surface dark:bg-surface-dark border rounded text-ink-text dark:text-ink-text-dark">Cancelar</button>
                                                                        <button onClick={() => handleSaveDescription()} className="text-sm px-4 py-2 bg-brand-500 text-white hover:bg-brand-600 rounded">Salvar</button>
                                                                    </div>
                                                                ) : null}

                                                                {/* Attachments dropzone */}
                                                                <div className="bg-surface dark:bg-surface-dark border border-[#eaeaea] rounded-lg p-4 shadow-sm">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Anexos</div>
                                                                        <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Aceita PDF, HTML, JPG, PNG</div>
                                                                    </div>

                                                                    <div className={`mt-3 p-4 rounded-md border-2 ${kanbanDropHover ? 'border-info/40 bg-info-soft dark:bg-info-soft-dark' : 'border-dashed border-line dark:border-line-dark bg-surface dark:bg-surface-dark'} text-center`} onDragOver={(e) => { e.preventDefault(); setKanbanDropHover(true); }} onDragLeave={() => setKanbanDropHover(false)} onDrop={handleDropAttachments}>
                                                                        <div className="flex items-center justify-center gap-3">
                                                                            <Paperclip className="w-5 h-5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                                                                            <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Arraste arquivos aqui ou clique para selecionar</div>
                                                                            <label className="ml-2 text-sm bg-surface dark:bg-surface-dark text-blue-600 dark:text-blue-300 px-3 py-1 rounded cursor-pointer">
                                                                                Selecionar
                                                                                <input type="file" accept=".pdf,.html,.htm,.jpg,.jpeg,.png" multiple className="hidden" onChange={handleSelectAttachments} />
                                                                            </label>
                                                                        </div>
                                                                    </div>

                                                                    {/* Existing attachments list */}
                                                                    <ul className="mt-4 space-y-2">
                                                                        {(kanbanOrder.attachments || []).map((att: any) => (
                                                                            <li key={att.id} className="flex items-center justify-between gap-3 bg-surface dark:bg-surface-dark border border-[#f3f3f3] rounded p-2">
                                                                                <div className="flex items-center gap-3 min-w-0">
                                                                                    <div className="w-10 h-10 rounded bg-surface-alt dark:bg-surface-dark-alt flex items-center justify-center text-ink-subtitle dark:text-ink-subtitle-dark text-sm"><ImageIcon className="w-5 h-5" /></div>
                                                                                    <div className="min-w-0">
                                                                                        <button onClick={async (e) => { e.preventDefault(); try { const { downloadUrl } = await apiService.getPresignedDownloadUrl(att.url); window.open(downloadUrl, '_blank'); } catch (err) { console.error(err); await showAlert('Erro ao baixar anexo.'); } }} className="text-sm text-blue-600 dark:text-blue-300 truncate max-w-[320px] text-left">{att.name}</button>
                                                                                        <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{att.size ? `${att.size} bytes` : ''}</div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <button onClick={async () => { const ok = await showConfirm('Remover este anexo?'); if (!ok) return; try { await apiService.deleteOrderAttachment(kanbanOrder.id, att.id); const all = await apiService.getOrders(); setOrders(all); const updated = all.find((o:any)=>o.id===kanbanOrder.id) || null; setKanbanOrder(updated as any); } catch (err) { console.error(err); await showAlert('Erro ao remover anexo.'); } }} className="text-sm text-red-600 dark:text-red-300">Remover</button>
                                                                                </div>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </div>

                                                            {/* Right column (activity) */}
                                                            <div className="md:col-span-5 flex flex-col">
                                                                <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Atividade</div>
                                                                <div className="mt-3 space-y-4 overflow-auto">
                                                                    {kanbanLoadingComments ? <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Carregando...</div> : (
                                                                        <div className="space-y-4">
                                                                            {kanbanComments.length === 0 ? <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Nenhuma atividade registrada.</div> : (
                                                                                                                                                kanbanComments.map(c => (
                                                                                                                                                    <div id={`comment-${c.id}`} key={c.id} className="flex items-start gap-3">
                                                                                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 font-semibold">{(c.userName || 'U').split(' ').map((s:string)=>s[0]).slice(0,2).join('')}</div>
                                                                                        <div className="flex-1">
                                                                                            <div className="flex items-center justify-between gap-2">
                                                                                                <div>
                                                                                                    <div className="text-sm font-medium text-ink-title dark:text-ink-title-dark">{c.userName || 'Usuário'}</div>
                                                                                                    <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{new Date(c.createdAt).toLocaleString('pt-BR')}</div>
                                                                                                </div>
                                                                                                <div>
                                                                                                        {(() => {
                                                                                                            const user = localStorage.getItem('user');
                                                                                                            const parsed = user ? JSON.parse(user) : null;
                                                                                                            const canDelete = parsed && (parsed.id === c.userId);
                                                                                                            if (!canDelete) return null;
                                                                                                            return (
                                                                                                                <button
                                                                                                                    onClick={async () => {
                                                                                                                        const ok = await showConfirm('Remover comentário?');
                                                                                                                        if (!ok) return;
                                                                                                                        try {
                                                                                                                            await apiService.deleteOrderComment(kanbanOrder.id, c.id);
                                                                                                                            const updatedComments = await apiService.getOrderComments(kanbanOrder.id);
                                                                                                                            setKanbanComments(updatedComments || []);
                                                                                                                            try { await showAlert('Comentário removido.'); } catch (_) {}
                                                                                                                        } catch (err: any) {
                                                                                                                            console.error('Erro ao remover comentário:', err);
                                                                                                                            const msg = err?.message || 'Erro ao remover comentário.';
                                                                                                                            try { await showAlert(msg); } catch (_) {}
                                                                                                                            // garantir estado consistente
                                                                                                                            try {
                                                                                                                                const refreshed = await apiService.getOrderComments(kanbanOrder.id);
                                                                                                                                setKanbanComments(refreshed || []);
                                                                                                                            } catch (_) { /* ignore */ }
                                                                                                                        }
                                                                                                                    }}
                                                                                                                    className="text-xs text-red-600 dark:text-red-300"
                                                                                                                >
                                                                                                                    🗑
                                                                                                                </button>
                                                                                                            );
                                                                                                        })()}
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="mt-2 bg-surface dark:bg-surface-dark border border-[#f3f3f3] rounded-lg p-3 text-sm text-ink-text dark:text-ink-text-dark shadow-sm">
                                                                                                {renderCommentContent(c.content)}
                                                                                                {c.meta && (c.meta as any).attachments && (c.meta as any).attachments.length > 0 && (
                                                                                                    <div className="mt-2 flex flex-wrap gap-2 w-full">
                                                                                                        {((c.meta as any).attachments as any[]).map((att: any, ai: number) => {
                                                                                                            const isImg = att.type && att.type.startsWith('image/');
                                                                                                            const isAudio = att.type && att.type.startsWith('audio/');
                                                                                                            if (isAudio) {
                                                                                                                return <CommentAttachmentAudio key={ai} s3Key={att.url} name={att.name} />;
                                                                                                            }
                                                                                                            return (
                                                                                                                <button key={ai} type="button" title={att.name}
                                                                                                                    onClick={async () => { try { const { downloadUrl } = await apiService.getPresignedDownloadUrl(att.url); window.open(downloadUrl, '_blank'); } catch (err) { console.error(err); } }}
                                                                                                                    className="border border-line dark:border-line-dark rounded overflow-hidden bg-surface dark:bg-surface-dark hover:shadow-sm transition-shadow">
                                                                                                                    {isImg ? (
                                                                                                                        <CommentAttachmentImage s3Key={att.url} name={att.name} />
                                                                                                                    ) : (
                                                                                                                        <div className="flex items-center gap-2 px-2 py-1 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-50">
                                                                                                                            <span>📎</span>
                                                                                                                            <span className="max-w-[140px] truncate">{att.name}</span>
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </button>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Rich comment composer */}
                                                                <div className="mt-4">
                                                                    {/* File previews */}
                                                                    {commentPreviews.length > 0 && (
                                                                        <div className="mb-2 flex flex-wrap gap-2 p-2 bg-surface-alt dark:bg-surface-dark-alt rounded-lg border border-line dark:border-line-dark">
                                                                            {commentPreviews.map((prev, idx) => (
                                                                                <div key={idx} className="relative group">
                                                                                    {prev.isImage ? (
                                                                                        <div className="relative">
                                                                                            <img src={prev.url} alt={prev.name} className="h-16 w-16 object-cover rounded-lg border border-line dark:border-line-dark cursor-pointer" title={prev.name} />
                                                                                            <button onClick={() => removeCommentFile(idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none shadow">✕</button>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="flex items-center gap-1.5 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg px-2 py-1 pr-6 relative max-w-[160px]">
                                                                                            <span className="text-ink-subtitle dark:text-ink-subtitle-dark">📎</span>
                                                                                            <span className="text-xs text-ink-text dark:text-ink-text-dark truncate">{prev.name}</span>
                                                                                            <button onClick={() => removeCommentFile(idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none shadow">✕</button>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Emoji picker */}
                                                                    {showEmojiPicker && (
                                                                        <div className="mb-2 p-2 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg shadow-sm flex flex-wrap gap-1">
                                                                            {COMMON_EMOJIS.map(emoji => (
                                                                                <button key={emoji} type="button" onClick={() => { setKanbanNewComment(prev => prev + emoji); setShowEmojiPicker(false); kanbanCommentRef.current?.focus(); }} className="text-lg hover:bg-surface-alt dark:bg-surface-dark-alt rounded p-0.5 leading-none">{emoji}</button>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Drop zone wrapper — only shown when NOT recording and no audio blob */}
                                                                    {!audioRecording && !audioBlob && (
                                                                    <div
                                                                        onDragOver={(e) => e.preventDefault()}
                                                                        onDrop={handleCommentDrop}
                                                                        className="border border-line dark:border-line-dark rounded-lg overflow-hidden focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-300"
                                                                    >
                                                                        <textarea
                                                                            ref={kanbanCommentRef}
                                                                            value={kanbanNewComment}
                                                                            onChange={handleKanbanCommentChange}
                                                                            onKeyDown={handleKanbanCommentKeyDown}
                                                                            onPaste={handleCommentPaste}
                                                                            placeholder="Escreva uma mensagem... (arraste arquivos, cole imagens, @mencione)"
                                                                            className="w-full min-h-[80px] p-3 text-sm outline-none resize-none bg-surface dark:bg-surface-dark"
                                                                        />
                                                                        {/* Toolbar */}
                                                                        <div className="flex items-center gap-1 px-2 py-1.5 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark">
                                                                            <button type="button" title="Emoji" onClick={() => setShowEmojiPicker(v => !v)} className="p-1.5 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:text-ink-title-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded text-base leading-none">😊</button>
                                                                            <label title="Imagem" className="p-1.5 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:text-ink-title-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded cursor-pointer leading-none" style={{ fontSize: '1rem' }}>
                                                                                🖼️
                                                                                <input ref={commentFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleCommentFileSelect} />
                                                                            </label>
                                                                            <label title="Arquivo (PDF, DOC, ZIP…)" className="p-1.5 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:text-ink-title-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded cursor-pointer leading-none" style={{ fontSize: '1rem' }}>
                                                                                📎
                                                                                <input type="file" multiple className="hidden" onChange={handleCommentFileSelect} />
                                                                            </label>
                                                                            {/* Mic button — click to start, click again to stop */}
                                                                            <button
                                                                                type="button"
                                                                                title="Gravar áudio"
                                                                                onClick={startRecording}
                                                                                className="p-1.5 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-red-600 hover:bg-red-50 rounded text-base leading-none"
                                                                            >🎙️</button>
                                                                            <span className="flex-1" />
                                                                            <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark">Arraste ou cole • @mencione</span>
                                                                        </div>
                                                                    </div>
                                                                    )}

                                                                    {/* Recording in progress UI */}
                                                                    {audioRecording && (
                                                                        <div className="border-2 border-red-300 bg-red-50 dark:bg-red-900/30 rounded-lg p-3 flex items-center gap-3">
                                                                            <span className="flex items-center gap-1.5 text-red-600 dark:text-red-300 font-medium text-sm">
                                                                                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse inline-block" />
                                                                                Gravando…
                                                                            </span>
                                                                            <span className="font-mono text-sm text-red-700 dark:text-red-300 tabular-nums">{formatAudioDuration(audioDuration)}</span>
                                                                            <span className="flex-1" />
                                                                            <button type="button" onClick={cancelRecording} title="Cancelar" className="px-2 py-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border rounded hover:bg-surface-alt dark:bg-surface-dark-alt">✕ Cancelar</button>
                                                                            <button type="button" onClick={stopRecording} title="Parar e revisar" className="px-3 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600 font-medium">⏹ Parar</button>
                                                                        </div>
                                                                    )}

                                                                    {/* Audio preview (after recording stopped) */}
                                                                    {!audioRecording && audioBlob && audioPreviewUrl && (
                                                                        <div className="border border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-3 flex flex-col gap-2">
                                                                            <div className="flex items-center gap-2 text-sm text-ink-text dark:text-ink-text-dark">
                                                                                <span>🎙️</span>
                                                                                <span className="font-medium">Áudio gravado</span>
                                                                                <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark ml-1">({formatAudioDuration(audioDuration)})</span>
                                                                                <span className="flex-1" />
                                                                                <button type="button" onClick={discardAudio} title="Descartar" className="text-xs text-red-500 dark:text-red-400 hover:underline">Descartar</button>
                                                                            </div>
                                                                            <audio src={audioPreviewUrl} controls className="w-full h-9" />
                                                                            <button
                                                                                type="button"
                                                                                disabled={kanbanPostingComment}
                                                                                onClick={sendAudioComment}
                                                                                className="w-full py-1.5 bg-brand-500 text-white rounded text-sm font-medium hover:bg-brand-600 disabled:opacity-60"
                                                                            >{kanbanPostingComment ? 'Enviando…' : '📤 Enviar áudio'}</button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Footer - fixed */}
                                                    <div className="sticky bottom-0 z-20 bg-surface dark:bg-surface-dark border-t border-[#eaeaea] px-6 py-3 flex items-center justify-end gap-3">
                                                        <button onClick={() => { setIsKanbanModalOpen(false); setKanbanOrder(null); clearCommentAttachments(); }} className="px-4 py-2 bg-surface dark:bg-surface-dark border rounded text-sm text-ink-text dark:text-ink-text-dark">Cancelar</button>
                                                        <button onClick={async () => {
                                                            if (!kanbanOrder) return;
                                                            if (!kanbanNewComment.trim() && commentFiles.length === 0) { await showAlert('✏️ Escreva uma mensagem ou adicione um arquivo antes de publicar.'); return; }
                                                            setKanbanPostingComment(true);
                                                            try {
                                                                const userRaw = localStorage.getItem('user');
                                                                const user = userRaw ? JSON.parse(userRaw) : null;
                                                                const commentContent = kanbanNewComment.trim() || (commentFiles.length > 0 ? `📎 ${commentFiles.length} arquivo(s) anexado(s)` : '');
                                                                const payload: any = { content: commentContent, userId: user?.id, userName: user?.name };
                                                                // Upload comment files and include in meta
                                                                const attachments = await uploadCommentFiles(kanbanOrder.id);
                                                                const mentions = kanbanMentions && kanbanMentions.length > 0 ? kanbanMentions.map((u:any) => u.id) : undefined;
                                                                payload.meta = { ...(mentions ? { mentions } : {}), ...(attachments.length > 0 ? { attachments } : {}) };
                                                                await apiService.createOrderComment(kanbanOrder.id, payload);
                                                                setKanbanNewComment('');
                                                                setKanbanMentions([]);
                                                                setShowMentionSuggestions(false);
                                                                clearCommentAttachments();
                                                                setKanbanComments(await apiService.getOrderComments(kanbanOrder.id));
                                                            } catch (err) { console.error('Erro ao publicar comentário:', err); await showAlert('Erro ao publicar comentário.'); }
                                                            finally { setKanbanPostingComment(false); }
                                                        }} className="px-4 py-2 bg-brand-500 text-white hover:bg-brand-600 rounded text-sm">{kanbanPostingComment ? 'Enviando...' : 'Publicar'}</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {showMentionSuggestions && mentionSuggestions.length > 0 && suggestionPortalStyle && createPortal(
                                            <div style={{ position: 'fixed', left: suggestionPortalStyle.left, top: suggestionPortalStyle.top, width: suggestionPortalStyle.width, transform: 'translateY(calc(-100% - 8px))' }} className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded shadow-lg z-60 max-h-44 overflow-auto">
                                                {mentionSuggestions.map((u, idx) => (
                                                    <button
                                                        key={u.id}
                                                        onMouseDown={(ev) => { ev.preventDefault(); insertMention(u); }}
                                                        className={`w-full text-left px-3 py-2 hover:bg-surface-alt dark:bg-surface-dark-alt ${idx === selectedSuggestionIndex ? 'bg-surface-alt dark:bg-surface-dark-alt' : ''}`}
                                                    >
                                                        <div className="text-sm font-medium text-ink-title dark:text-ink-title-dark">{u.name}</div>
                                                        <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{u.email}</div>
                                                    </button>
                                                ))}
                                            </div>,
                                            document.body
                                        )}
                                            </section>
                                        ) : viewMode === 'cards' ? (
              /* Modo CARDS — pixel-perfect com .lcard */
              <div className="p-4 md:p-6">
                {filteredOrders.length === 0 ? (
                  <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-ink-placeholder dark:text-ink-placeholder-dark" />
                    <p>Nenhum empenho ou O.S. encontrada.</p>
                  </div>
                ) : (
                  <div className="lic-cards">
                    {filteredOrders.map(order => {
                      const daysLeft = daysUntilCalendarDate(order.deliveryDate);
                      const tone = daysLeft == null ? 'ok'
                        : daysLeft < 0 ? 'past'
                        : daysLeft <= 3 ? 'crit'
                        : daysLeft <= 10 ? 'warn'
                        : daysLeft <= 20 ? 'info' : 'ok';
                      const statusPillCls = order.status === 'OVERDUE' ? 'crit'
                        : order.status === 'PENDING' ? 'warn'
                        : order.status === 'COMPLETED' || order.status === 'DELIVERED' ? 'succ'
                        : order.status === 'IN_PROGRESS' || order.status === 'APPROVED' ? 'info'
                        : '';
                      const prazoColorVar = tone === 'crit' ? 'var(--color-danger)'
                        : tone === 'warn' ? 'var(--color-warning)'
                        : tone === 'info' ? 'var(--color-info)'
                        : 'var(--color-ink-title)';
                      return (
                        <div key={order.id} className="lcard" onClick={() => { setKanbanOrder(order); setIsKanbanModalOpen(true); }}>
                          <div className={`rail ${tone === 'past' ? 'crit' : tone}`} />
                          <div className="lcard-head">
                            <span className="num">{order.number}</span>
                            <span className="uf-tag" style={{ marginLeft: 6 }}>{order.documentType === 'EMPENHO' ? 'EMP' : order.documentType === 'ORDEM_SERVICO' ? 'O.S.' : order.documentType === 'AUTORIZACAO' ? 'AUT' : 'OUT'}</span>
                          </div>
                          <h4>{order.title}</h4>
                          <div className="orgao">
                            <Building2 style={{ width: 11, height: 11, flexShrink: 0, opacity: .7 }} />
                            <span className="txt">{order.supplier || '—'}</span>
                          </div>
                          <div className="lcard-meta">
                            <div>
                              <div className="k">Prazo</div>
                              <div className="v" style={{ color: prazoColorVar, fontWeight: 600 }}>
                                {daysLeft == null ? '—' : daysLeft < 0 ? `Vencido ${Math.abs(daysLeft)}d` : `${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`}
                                {order.deliveryDate && ` · ${formatCalendarDate(order.deliveryDate)}`}
                              </div>
                            </div>
                            <div>
                              <div className="k">Valor</div>
                              <div className="v">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(order.value)}</div>
                            </div>
                            <div>
                              <div className="k">Emissão</div>
                              <div className="v" style={{ fontSize: 12 }}>{formatCalendarDate(order.requestDate)}</div>
                            </div>
                            <div>
                              <div className="k">Tipo</div>
                              <div className="v" style={{ fontSize: 12 }}>{order.type === 'PRODUCT' ? 'Produto' : 'Serviço'}</div>
                            </div>
                          </div>
                          <div className="lcard-foot">
                            <div className="pills">
                              <span className={`lic-pill ${statusPillCls}`}>{order.status}</span>
                              {order.tenderId && <span className="lic-pill info">Vinculada</span>}
                            </div>
                            <ArrowRight style={{ width: 14, height: 14, color: 'var(--color-ink-subtitle)' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
                                        ) : (
              /* Modo LISTA — pixel-perfect com .lic-row */
              <div className="p-4 md:p-6">
                {filteredOrders.length === 0 ? (
                  <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-ink-placeholder dark:text-ink-placeholder-dark" />
                    <p>Nenhum empenho ou O.S. encontrada.</p>
                  </div>
                ) : (
                  <div className="lic-list">
                    {filteredOrders.map(order => {
                      const daysLeft = daysUntilCalendarDate(order.deliveryDate);
                      const tone = daysLeft == null ? 'ok'
                        : daysLeft < 0 ? 'past'
                        : daysLeft <= 3 ? 'crit'
                        : daysLeft <= 10 ? 'warn'
                        : daysLeft <= 20 ? 'info' : 'ok';
                      const railCls = order.status === 'OVERDUE' ? 'critical'
                        : order.status === 'PENDING' ? 'alta'
                        : order.status === 'IN_PROGRESS' || order.status === 'APPROVED' ? 'media'
                        : 'baixa';
                      const statusPillCls = order.status === 'OVERDUE' ? 'crit'
                        : order.status === 'PENDING' ? 'warn'
                        : order.status === 'COMPLETED' || order.status === 'DELIVERED' ? 'succ'
                        : order.status === 'IN_PROGRESS' || order.status === 'APPROVED' ? 'info'
                        : '';
                      const prazoStyle: React.CSSProperties = tone === 'crit'
                        ? { color: 'var(--color-danger)' }
                        : tone === 'warn'
                          ? { color: 'var(--color-warning)' }
                          : {};
                      const docBadge = order.documentType === 'EMPENHO' ? 'EMP'
                        : order.documentType === 'ORDEM_SERVICO' ? 'O.S.'
                        : order.documentType === 'AUTORIZACAO' ? 'AUT' : 'OUT';
                      return (
                        <div key={order.id} className="lic-row" onClick={() => { setKanbanOrder(order); setIsKanbanModalOpen(true); }}>
                          <span className={`rail ${railCls}`} />
                          <div className={`countdown ${tone}`}>
                            <div className="n">{daysLeft == null ? '—' : daysLeft < 0 ? '—' : Math.abs(daysLeft)}</div>
                            <div className="u">{daysLeft == null ? '' : daysLeft < 0 ? 'venc.' : daysLeft === 0 ? 'HOJE' : daysLeft === 1 ? 'DIA' : 'DIAS'}</div>
                          </div>
                          <div className="main">
                            <div className="title">
                              <span>{order.number} — {order.title}</span>
                            </div>
                            <div className="sub">
                              <Building2 style={{ width: 11, height: 11 }} />
                              <span className="orgao">{order.supplier || '—'}</span>
                              <span className="sep">·</span>
                              <span>{order.type === 'PRODUCT' ? 'Produto' : 'Serviço'}</span>
                              {order.tenderId && (
                                <>
                                  <span className="sep">·</span>
                                  <span style={{ color: 'var(--color-info)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    <LinkIcon style={{ width: 10, height: 10 }} /> Vinculada
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <span className="uf-tag">{docBadge}</span>
                          <div className="dates">
                            <span className="label">Emissão</span>
                            <span className="val">{formatCalendarDate(order.requestDate)}</span>
                          </div>
                          <div className="dates">
                            <span className="label">Prazo final</span>
                            <span className="val" style={{ ...prazoStyle, fontWeight: 600 }}>
                              {formatCalendarDate(order.deliveryDate)}
                            </span>
                          </div>
                          <div>
                            <span className={`lic-pill ${statusPillCls}`}>{order.status}</span>
                          </div>
                          <div className="checklist-mini">
                            <div className="label">
                              <span>Valor</span>
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-ink-title)', fontVariantNumeric: 'tabular-nums' }}>
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(order.value)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="lic-more-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setActionsAnchorRect({ top: r.bottom, left: r.right });
                                setOpenActionsId(openActionsId === order.id ? null : order.id);
                              }}
                              aria-label="Mais opções"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {openActionsId === order.id && actionsAnchorRect && createPortal(
                              <div className="w-40 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md shadow-lg z-50 actions-menu" style={{ position: 'fixed', top: actionsAnchorRect.top + 8, left: Math.max(actionsAnchorRect.left - 160, 8) }} onClick={(e) => e.stopPropagation()}>
                                {(order.file || order.fileUrl) ? (
                                  <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleDownload(order); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center gap-2"><Paperclip className="w-4 h-4" />Anexo</button>
                                ) : (
                                  <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); }} className="w-full text-left px-3 py-2 text-sm text-ink-placeholder dark:text-ink-placeholder-dark cursor-default flex items-center gap-2"><Paperclip className="w-4 h-4" />Anexo</button>
                                )}
                                <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleOpenModal(order); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center gap-2"><Edit2 className="w-4 h-4" />Editar</button>
                                <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleDelete(order.id, order.number); }} className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"><Trash2 className="w-4 h-4" />Deletar</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
      )}

      {/* Modal Criar/Editar */}
      {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                  <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                      <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          {editingOrder ? 'Editar Registro' : 'Registrar Empenho / O.S.'}
                      </h3>
                      <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                          <XCircle className="w-5 h-5" />
                      </button>
                  </div>

                  <div className="p-6 overflow-y-auto space-y-4">

                      {/* --- ÁREA DE IMPORTAÇÃO INTELIGENTE (NOVO) --- */}
                      {!editingOrder && (
                          <div
                              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer relative overflow-hidden group ${
                                  aiSuccess ? 'border-green-300 bg-green-50 dark:bg-green-900/30' : 'border-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100'
                              }`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={onDropFile}
                          >
                              {isExtracting ? (
                                  <div className="flex flex-col items-center justify-center py-4">
                                      <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-300 animate-spin mb-2" />
                                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Lendo Empenho/OS com IA...</p>
                                      <p className="text-xs text-blue-600 dark:text-blue-300">Extraindo número, valores e datas</p>
                                  </div>
                              ) : aiSuccess ? (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300 mb-2" />
                                      <p className="text-sm font-bold text-green-800 dark:text-green-200">Leitura Concluída!</p>
                                      <p className="text-xs text-green-600 dark:text-green-300">Confira os dados abaixo e salve.</p>
                                  </div>
                              ) : (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <div className="bg-surface dark:bg-surface-dark p-3 rounded-full shadow-sm mb-3">
                                          <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                                      </div>
                                      <h4 className="font-bold text-blue-900 mb-1">Importação Inteligente</h4>
                                      <p className="text-xs text-blue-700 dark:text-blue-300 max-w-xs mx-auto mb-3">
                                          Arraste o PDF, Imagem ou HTML do Empenho/OS aqui para preenchimento automático.
                                      </p>
                                      <label className="text-xs bg-brand-500 text-white px-3 py-1.5 rounded-lg hover:bg-brand-600 transition-colors cursor-pointer shadow-sm">
                                          Selecionar Arquivo
                                          <input
                                              type="file"
                                              accept=".pdf,.html,.htm,.jpg,.jpeg,.png"
                                              className="hidden"
                                              onChange={(e) => {
                                                  if (e.target.files && e.target.files[0]) {
                                                      handleAiExtraction(e.target.files[0]);
                                                  }
                                              }}
                                          />
                                      </label>
                                  </div>
                              )}
                          </div>
                      )}

                      {/* Vínculo com Licitação */}
                      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-100">
                          <label className="block text-xs font-bold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                              <LinkIcon className="w-3 h-3" />
                              Vincular Licitação Ganha (Opcional)
                          </label>
                          <select
                              className="w-full border border-blue-200 dark:border-blue-800/60 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark"
                              value={formData.tenderId || ''}
                              onChange={e => handleTenderSelection(e.target.value)}
                          >
                              <option value="">Selecione uma licitação ganha...</option>
                              {wonTenders.map(t => (
                                  <option key={t.id} value={t.id}>
                                      ID: {t.id} - {t.title.substring(0, 60)}...
                                  </option>
                              ))}
                          </select>
                          <p className="text-[10px] text-blue-600 dark:text-blue-300 mt-1">Ao selecionar, o Órgão e o Título serão sugeridos automaticamente.</p>
                      </div>

                      {/* Linha 1 */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Tipo de Documento</label>
                              <select
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                  value={formData.documentType || ''}
                                  onChange={e => setFormData({...formData, documentType: e.target.value as OrderDocumentType})}
                              >
                                  <option value="EMPENHO">Nota de Empenho</option>
                                  <option value="ORDEM_SERVICO">Ordem de Serviço (O.S.)</option>
                                  <option value="AUTORIZACAO">Autorização de Fornecimento</option>
                                  <option value="OUTRO">Outro</option>
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Número do Documento *</label>
                              <input
                                  type="text"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  placeholder="Ex: 2023NE000123"
                                  value={formData.number}
                                  onChange={e => setFormData({...formData, number: e.target.value})}
                              />
                          </div>
                      </div>

                      {/* Linha 2 */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Objeto / Título *</label>
                          <input
                              type="text"
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                              placeholder="Ex: Aquisição de Material de TI"
                              value={formData.title}
                              onChange={e => setFormData({...formData, title: e.target.value})}
                          />
                      </div>

                      {/* Linha 3 */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Órgão Emissor (Cliente) *</label>
                              <input
                                  type="text"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={formData.supplier}
                                  onChange={e => setFormData({...formData, supplier: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Valor (R$) *</label>
                              <MoneyInput
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={typeof formData.value === 'number' ? formData.value : null}
                                  onValueChange={(v) => setFormData({...formData, value: v ?? 0})}
                              />
                          </div>
                      </div>

                      {/* Linha 4 */}
                      <div className="grid grid-cols-3 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data Emissão</label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={formData.requestDate}
                                  onChange={e => setFormData({...formData, requestDate: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Prazo Execução/Entrega</label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={formData.deliveryDate || ''}
                                  onChange={e => setFormData({...formData, deliveryDate: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Status</label>
                              <select
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                  value={formData.status || ''}
                                  onChange={e => setFormData({...formData, status: e.target.value as OrderStatus})}
                              >
                                  <option value="OPEN">No prazo</option>
                                  <option value="OVERDUE">Vencido</option>
                                  <option value="DELIVERED">Entregue</option>
                                  <option value="PENDING">Pendente</option>
                                  <option value="IN_PROGRESS">Em Execução</option>
                                  <option value="COMPLETED">Entregue</option>
                                  <option value="CANCELLED">Cancelado</option>
                              </select>
                          </div>
                      </div>

                      {/* Seleção de Empresa Executora */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Empresa Executora (Minha Empresa)</label>
                          <select
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                              value={formData.companyId || ''}
                              onChange={e => setFormData({...formData, companyId: e.target.value})}
                          >
                              <option value="" disabled>Selecione a empresa...</option>
                              {companies.map(c => (
                                  <option key={c.id} value={c.id}>{c.tradeName} ({c.cnpj})</option>
                              ))}
                          </select>
                      </div>

                      {/* Importação de Arquivo Manual */}
                      <div className="bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark rounded-lg p-4">
                          <label className="block text-sm font-semibold text-ink-title dark:text-ink-title-dark mb-2 flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              {selectedFile ? 'Arquivo Selecionado' : 'Anexar Documento Manualmente'}
                          </label>
                                                <input
                                                            type="file"
                                                            accept=".pdf,.html,.htm,.jpg,.jpeg,.png"
                                                            multiple
                                                            onChange={handleFileUpload}
                                                            className="block w-full text-sm text-ink-subtitle dark:text-ink-subtitle-dark file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-surface dark:bg-surface-dark file:text-blue-700 hover:file:bg-blue-50 transition-all cursor-pointer"
                                                    />
                          {editingOrder?.file && !selectedFile && (
                              <div className="mt-2 text-xs text-green-600 dark:text-green-300 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Arquivo atual: <strong>{editingOrder.file.name}</strong>
                              </div>
                          )}
                          {selectedFile && (
                              <div className="mt-2 text-xs text-blue-600 dark:text-blue-300 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Novo arquivo: <strong>{selectedFile.name}</strong>
                              </div>
                          )}
                          {/* Pre-save multiple files (create flow) */}
                          {!editingOrder && selectedFiles.length > 0 && (
                              <div className="mt-3 border-t pt-3">
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-2">Arquivos selecionados</label>
                                  <ul className="space-y-2">
                                      {selectedFiles.map((f, idx) => (
                                          <li key={idx} className="flex items-center justify-between text-sm">
                                              <div className="flex items-center gap-2">
                                                  <Paperclip className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                                  <span className="text-sm text-ink-text dark:text-ink-text-dark">{f.name}</span>
                                              </div>
                                              <div>
                                                  <button onClick={() => removeSelectedFile(idx)} className="text-sm text-red-600 dark:text-red-300 hover:underline">Remover</button>
                                              </div>
                                          </li>
                                      ))}
                                  </ul>
                              </div>
                          )}
                          {/* Lista de anexos existentes (quando editando) */}
                          {editingOrder && editingOrder.attachments && editingOrder.attachments.length > 0 && (
                              <div className="mt-3 border-t pt-3">
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-2">Anexos</label>
                                  <ul className="space-y-2">
                                      {editingOrder.attachments.map((att: any) => (
                                          <li key={att.id} className="flex items-center justify-between text-sm">
                                              <div className="flex items-center gap-2">
                                                  <Paperclip className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                                  <a
                                                      href="#"
                                                      onClick={async (e) => {
                                                          e.preventDefault();
                                                          try {
                                                              const { downloadUrl } = await apiService.getPresignedDownloadUrl(att.url);
                                                              window.open(downloadUrl, '_blank');
                                                          } catch (err) {
                                                              console.error(err);
                                                              await showAlert('Erro ao baixar anexo.');
                                                          }
                                                      }}
                                                      className="text-blue-600 dark:text-blue-300 hover:underline"
                                                  >
                                                      {att.name}
                                                  </a>
                                                  <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark ml-2">{att.size ? `${att.size} bytes` : ''}</span>
                                              </div>
                                              <div>
                                                  <button onClick={() => handleDeleteAttachment(att.id)} className="text-sm text-red-600 dark:text-red-300 hover:underline">Remover</button>
                                              </div>
                                          </li>
                                      ))}
                                  </ul>
                              </div>
                          )}
                      </div>
                  </div>

                  <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                      <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">Cancelar</button>
                      <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm">
                          <Save className="w-4 h-4" /> Salvar Registro
                      </button>
                  </div>
              </div>
          </div>
            )}
                                        </div>
                                </div>
                        </div>
    );
};

export default OrderManager;
